import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Upload, Trash2, Loader2, FileSearch, Zap } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/toastContext';
import { useConfirm } from '../../components/ui/useConfirm';

interface DocItem {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  parse_status: string;
  created_at: string;
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusLabel = (status: string) => {
  switch (status) {
    case 'parsed':
      return <span className="badge success">已解析</span>;
    case 'failed':
      return <span className="badge error">解析失败</span>;
    default:
      return <span className="badge secondary">待解析</span>;
  }
};

/** 提取后端错误文案（detail 优先，其次 Error.message） */
const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

export default function DocumentTeaching() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState<DocItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [parsingId, setParsingId] = useState<string | null>(null);
  const [vectorizingId, setVectorizingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!agentId) return;
    try {
      const res = await apiClient.get(`/agents/${agentId}/documents`);
      setDocuments(res.data || []);
    } catch (e) {
      console.error('Failed to load documents:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadDocuments();
    };
    tick();
  }, [loadDocuments]);

  const upload = async () => {
    if (!file || !agentId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post(`/agents/${agentId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('文档上传成功');
      setFile(null);
      await loadDocuments();
    } catch (e) {
      toast.error('上传失败: ' + errorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const parseDoc = async (docId: string) => {
    setParsingId(docId);
    try {
      await apiClient.post(`/documents/${docId}/parse`);
      toast.success('文档解析完成');
      await loadDocuments();
    } catch (e) {
      toast.error('解析失败: ' + errorMessage(e));
    } finally {
      setParsingId(null);
    }
  };

  const vectorizeDoc = async (docId: string) => {
    setVectorizingId(docId);
    try {
      const res = await apiClient.post(`/documents/${docId}/vectorize`);
      const vec = res.data.vectorization;
      toast.success(`向量化完成: ${vec?.chunks || 0} 个片段已入库`);
      await loadDocuments();
    } catch (e) {
      toast.error('向量化失败: ' + errorMessage(e));
    } finally {
      setVectorizingId(null);
    }
  };

  const deleteDoc = async (docId: string) => {
    if (!(await confirm({ title: '确定要删除这个文档吗？', danger: true }))) return;
    setDeletingId(docId);
    try {
      await apiClient.delete(`/documents/${docId}`);
      toast.success('文档已删除');
      await loadDocuments();
    } catch (e) {
      toast.error('删除失败: ' + errorMessage(e));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
        <ArrowLeft size={16} /> 返回概览
      </button>

      <div className="grid grid-cols-3 gap-4">
        {/* Upload panel */}
        <div className="col-span-1">
          <div className="card">
            <h3><Upload size={18} className="inline mr-1" /> 上传文档</h3>
            <p className="text-muted text-sm mt-2">
              上传论文、手册、规范等，系统将自动提取知识点。
            </p>

            <div className="flex flex-col gap-3 mt-4">
              <input
                type="file"
                accept=".pdf,.docx,.txt,.md"
                onChange={e => setFile(e.target.files?.[0] || null)}
              />
              {file && (
                <div className="text-sm">
                  <span className="text-muted">已选择: </span>
                  <strong>{file.name}</strong>
                  <span className="text-muted ml-2">({formatSize(file.size)})</span>
                </div>
              )}
              <button
                className="primary"
                onClick={upload}
                disabled={!file || uploading}
              >
                {uploading ? <><Loader2 size={14} className="inline animate-spin mr-1" /> 上传中...</> : <><Upload size={14} className="inline mr-1" /> 上传文档</>}
              </button>
            </div>

            <div className="mt-4 text-muted text-xs">
              <p>支持格式：PDF、DOCX、TXT、Markdown</p>
            </div>
          </div>
        </div>

        {/* Document list */}
        <div className="col-span-2">
          <div className="card">
            <h3><FileText size={18} className="inline mr-1" /> 文档列表</h3>

            {loading ? (
              <div className="loading mt-4"><div className="spinner" /></div>
            ) : documents.length === 0 ? (
              <div className="empty mt-4">
                <p className="text-muted">暂无文档，请从左侧上传</p>
              </div>
            ) : (
              <div className="list mt-4">
                {documents.map(doc => (
                  <div key={doc.id} className="list-item">
                    <div className="list-item-content">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-primary" />
                        <strong>{doc.file_name}</strong>
                        <span className="badge secondary text-xs">{doc.file_type}</span>
                        {statusLabel(doc.parse_status)}
                      </div>
                      <div className="text-muted text-sm mt-1">
                        <span>{formatSize(doc.file_size)}</span>
                        <span className="ml-2">{new Date(doc.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="list-item-actions" style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="secondary text-xs"
                        onClick={() => parseDoc(doc.id)}
                        disabled={parsingId === doc.id || doc.parse_status === 'parsed'}
                        title="解析文档"
                      >
                        {parsingId === doc.id ? (
                          <Loader2 size={12} className="inline animate-spin" />
                        ) : (
                          <FileSearch size={12} className="inline" />
                        )}
                      </button>
                      <button
                        className="secondary text-xs"
                        onClick={() => vectorizeDoc(doc.id)}
                        disabled={vectorizingId === doc.id || doc.parse_status !== 'parsed'}
                        title="向量化入库"
                      >
                        {vectorizingId === doc.id ? (
                          <Loader2 size={12} className="inline animate-spin" />
                        ) : (
                          <Zap size={12} className="inline" />
                        )}
                      </button>
                      <button
                        className="secondary text-xs"
                        onClick={() => deleteDoc(doc.id)}
                        disabled={deletingId === doc.id}
                        title="删除文档"
                      >
                        {deletingId === doc.id ? (
                          <Loader2 size={12} className="inline animate-spin" />
                        ) : (
                          <Trash2 size={12} className="inline text-error" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {dialog}
    </div>
  );
}
